import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import Axios from 'axios';
import ScrollableTable from '@/shared/components/table/ScrollableTable';
import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import config from '@/config';
import SignalRService from '@/shared/services/SignalRService';
import { FaCog, FaFileExcel } from 'react-icons/fa';
import moment from 'moment';
import ColumnHeader from '@/shared/components/table/ColumnHeader';
import SimplePanel from '@/shared/components/SimplePanel';
import handleHttpError from '@/shared/services/HandleHttpError';
import ToastrService from '@/shared/services/ToastrService';
import authService, { privileges } from '@/shared/services/AuthService';
import YesNoModal from '@/shared/components/YesNoModal';
import Checkbox from '@/shared/components/input/Checkbox';
import Button from '@/shared/components/Button';
import { injectStateService, StateService } from '@/shared/services/StateService';
import CheckboxListModal from '@/shared/components/CheckboxListModal';
import {
  handleHiddenColumnsSelection,
  initHiddenColumnsState,
} from '@/shared/utils/hiddenColumnsHelper';
import OkCancelModal from '@/shared/components/OkCancelModal';
import { CanRead } from '@/shared/components/security';
import BudgetOverviewFilters, { defaultFilters } from './BudgetOverviewFilters';
import messages from './budgetMessages';
import BudgetRow from './BudgetRow';
import { hasAssignments } from '../../../shared/components/security/HasAssignments';
import { clearBudget, mapBudgetOverview, noActuals } from './budgetHelpers';
import GlobalActionsButton from './GlobalActionsButton';
import CopyBudgetModal from './modals/CopyBudgetModal';
import UpdateBudgetAmountModal from './modals/UpdateBudgetAmountModal';
import UpdateBudgetHoursModal from './modals/UpdateBudgetHoursModal';
import BigBudgetGraph from './BigBudgetGraph';

const columnSelectionStorageKey = 'BudgetOverviewColumns';

const columnDefinitions = [
  { id: 'budgetAmount', message: messages.BudgetAmount, visibleByDefault: true },
  { id: 'actualAmount', message: messages.ActualAmount, visibleByDefault: true },
  { id: 'remainingAmount', message: messages.RemainingAmount, visibleByDefault: false },
  { id: 'budgetAmountUsedPct', message: messages.BudgetAmountUsedPct, visibleByDefault: true },
  { id: 'budgetHours', message: messages.BudgetHours, visibleByDefault: true },
  { id: 'actualHours', message: messages.ActualHours, visibleByDefault: true },
  { id: 'remainingHours', message: messages.RemainingHours, visibleByDefault: false },
  {
    id: 'plannedDuration',
    message: messages.PlannedDurationHours,
    visibleByDefault: true,
    enabled: () => authService.hasFeature('CapacityPlanning'),
  },
  {
    id: 'budgetVsPlannedDeviationPct',
    message: messages.BudgetVsPlannedDeviationPct,
    visibleByDefault: false,
    enabled: () => authService.hasFeature('CapacityPlanning'),
  },
  { id: 'budgetHoursUsedPct', message: messages.BudgetHoursUsedPct, visibleByDefault: true },
];

class BudgetOverviewContainer extends Component {
  constructor(props) {
    super(props);
    const { searchParams = {} } = props;

    const { budgetFilters: sessionFilters = {}, ...sessionParams } =
      JSON.parse(sessionStorage.getItem('BudgetOverviewParams')) || {};
    this.state = {
      allSelected: false,
      items: [],
      initialSearchParams: {
        // Set default filters
        sortKey: 'relation.displayName, periodFrom',
        pageSize: 50,
        desc: false,
        budgetFilters: {
          ...defaultFilters,
          ...sessionFilters,
          ...searchParams,
        },
        // Override with session storage params
        ...sessionParams,
      },
      itemToDelete: null,
      showUpdateBudgetHoursModal: false,
      showUpdateBudgetAmountModal: false,
      showCopyBudgetModal: false,
      graphModalData: null,
      totals: null,
      showColumnSelectionModal: false,
      columnStatus: {},
    };
    const { hideableColumns, columnStatus } = initHiddenColumnsState(
      columnSelectionStorageKey,
      columnDefinitions,
      props.intl,
    );
    this.state.hideableColumns = hideableColumns;
    this.state.columnStatus = columnStatus;
  }

  componentDidMount() {
    SignalRService.subscribe('jobUpdated', this.jobUpdateHandler);
  }

  componentWillUnmount() {
    SignalRService.unsubscribe('jobUpdated', this.jobUpdateHandler);
    if (this.searchToken && !this.searchToken.token.reason) {
      this.searchToken.cancel('Operation canceled due to leaving page.');
    }
    if (this.totalsToken && !this.totalsToken.token.reason) {
      this.totalsToken.cancel('Operation canceled due to leaving page.');
    }
  }

  loadItems = searchParams => {
    if (this.searchToken && !this.searchToken.token.reason) {
      this.searchToken.cancel('Operation canceled due to new request.');
    }
    this.searchToken = Axios.CancelToken.source();

    sessionStorage.setItem(
      'BudgetOverviewParams',
      JSON.stringify({
        desc: searchParams.desc,
        sortKey: searchParams.sortKey,
        budgetFilters: searchParams.budgetFilters,
      }),
    );

    const body = {
      ...searchParams.budgetFilters,
    };

    if (!searchParams.page) {
      this.loadTotals(searchParams);
    }

    return api.base
      .post('/api/budget/overview', body, {
        params: {
          desc: searchParams.desc,
          sort: searchParams.sortKey,
          page: searchParams.page,
          take: searchParams.pageSize,
        },
        cancelToken: this.searchToken.token,
      })
      .then(({ data }) => {
        const { allSelected } = this.state;
        const newLines = data.map(x => mapBudgetOverview(x, allSelected));

        this.setState(state => ({
          items: searchParams.page > 0 ? [...state.items, ...newLines] : [...newLines],
          currentSearchParams: searchParams,
        }));

        return newLines;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
        // Return empty array for cancelled requests so ScrollableTable can handle it properly
        return [];
      });
  };

  loadTotals = searchParams => {
    const { intl } = this.props;
    const { currentSearchParams } = this.state;
    const params = searchParams || currentSearchParams;
    if (!params) return Promise.resolve();

    if (this.totalsToken && !this.totalsToken.token.reason) {
      this.totalsToken.cancel('Operation canceled due to new request.');
    }
    this.totalsToken = Axios.CancelToken.source();

    return api.base
      .post(
        '/api/budget/Totals',
        { ...params.budgetFilters },
        { cancelToken: this.totalsToken.token },
      )
      .then(({ data }) => this.setState({ totals: data }))
      .catch(error => {
        if (Axios.isCancel(error)) return;
        handleHttpError(intl)(error);
      });
  };

  filtersChanged = filters => {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        budgetFilters: filters,
      },
    }));
  };

  onUpdateBudget = updateViewModel => {
    const { intl } = this.props;

    return api.base.put(`${config.appUrls.api}/api/budget/`, updateViewModel).then(({ data }) => {
      ToastrService.changesSaved(intl);
      this.setState(({ items }) => ({
        items: items.map(x => {
          if (x.invoiceStream.id === data.invoiceStreamId) {
            return { ...x, budget: { ...x.budget, ...noActuals(data) } };
          }
          return x;
        }),
      }));
      this.loadTotals();
    }, handleHttpError(intl));
  };
  onDeleteBudget = e => this.setState({ itemToDelete: e });

  doDelete = () => {
    const { itemToDelete } = this.state;
    const { intl } = this.props;
    api.base.delete(`${config.appUrls.api}/api/Budget/${itemToDelete.budget.id}`).then(() => {
      ToastrService.success();
      this.setState(
        ({ items, initialSearchParams: { budgetFilters: { withBudget = null } = {} } = {} }) => ({
          items:
            withBudget === true
              ? items.filter(
                  x =>
                    x.invoiceStream.id !== itemToDelete.invoiceStream.id &&
                    (x.budget === null || x.budget.id !== itemToDelete.budget.id),
                )
              : items.map(x => {
                  if (x.invoiceStream.id !== itemToDelete.invoiceStream.id) return x;
                  if (x.budget === null || x.budget.id !== itemToDelete.budget.id) return x;
                  return {
                    ...x,
                    budget: clearBudget(x.budget),
                    amountPerMonth: null,
                    hoursPerMonth: null,
                  }; // Clear budget but keep invoice stream in list
                }),
          itemToDelete: null,
        }),
      );
      this.loadTotals();
    }, handleHttpError(intl));
  };

  isGlobalActionsDisabled = () => {
    const { items, allSelected } = this.state;
    if (!items || !items.length) return true;
    if (allSelected || items.some(x => x.selected)) return false;

    return true;
  };
  handleSelectAll = () => {
    this.setState(({ items, allSelected }) => ({
      allSelected: !allSelected,
      items: items.map(x => (x.selected !== allSelected ? x : { ...x, selected: !allSelected })),
    }));
  };
  handleSelect = invoiceStreamId => {
    if (!invoiceStreamId) return;
    this.setState(({ items }) => ({
      items: items.map(x =>
        x.invoiceStream.id === invoiceStreamId
          ? {
              ...x,
              selected: !x.selected,
            }
          : x,
      ),
    }));
  };

  getCountSelectedRowsFormatted() {
    const { allSelected, items } = this.state;
    const { intl } = this.props;
    if (allSelected) {
      return intl.formatMessage(messages.AllBudgetsSelected);
    }

    const count = items ? items.filter(x => x.selected === true).length : 0;
    return intl.formatMessage(messages.CountBudgetsSelected, { count });
  }

  getInvoiceStreamSelection = () => {
    const { items, allSelected } = this.state;

    // Get all ids that don't match the select all checkbox
    const invoiceStreamIds = items
      ? items
          .filter(x => x.selected !== allSelected && x.invoiceStream && x.invoiceStream.id)
          .map(x => x.invoiceStream.id)
      : [];

    return {
      invoiceStreamIds,
      selectAll: allSelected,
    };
  };
  getBudgetFilters = () => {
    const { currentSearchParams, initialSearchParams } = this.state;
    const budgetFilters =
      (currentSearchParams && currentSearchParams.budgetFilters) ||
      (initialSearchParams && initialSearchParams.budgetFilters) ||
      {};

    return {
      ...budgetFilters,
    };
  };

  updateInvoiceStreamAmountBatch = viewModel => {
    const selection = this.getInvoiceStreamSelection();
    const { currentSearchParams } = this.state;
    const currentFilters = currentSearchParams.budgetFilters;

    const bodyParams = {
      ...selection,
      ...viewModel,
      filters: currentFilters,
    };

    const { stateService, intl } = this.props;
    return api.base
      .post(`${config.appUrls.api}/api/budget/BatchUpdateBudget`, bodyParams)
      .then(({ data }) => {
        this.setState({ showUpdateBudgetAmountModal: false });

        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;

        ToastrService.info(intl.formatMessage(messages.GlobalActionStarted), undefined, onClick);
      })
      .catch(handleHttpError(intl));
  };
  updateInvoiceStreamHoursBatch = viewModel => {
    const selection = this.getInvoiceStreamSelection();
    const { currentSearchParams } = this.state;
    const currentFilters = currentSearchParams.budgetFilters;

    const bodyParams = {
      ...selection,
      ...viewModel,
      filters: currentFilters,
    };

    const { stateService, intl } = this.props;
    return api.base
      .post(`${config.appUrls.api}/api/budget/BatchUpdateBudgetHours`, bodyParams)
      .then(({ data }) => {
        this.setState({ showUpdateBudgetHoursModal: false });

        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;

        ToastrService.info(intl.formatMessage(messages.GlobalActionStarted), undefined, onClick);
      })
      .catch(handleHttpError(intl));
  };
  copyBudgetBatch = viewModel => {
    const selection = this.getInvoiceStreamSelection();
    const { currentSearchParams } = this.state;
    const currentFilters = currentSearchParams.budgetFilters;

    const bodyParams = {
      ...selection,
      ...viewModel,
      filters: currentFilters,
    };

    const { stateService, intl } = this.props;
    return api.base
      .post(`${config.appUrls.api}/api/budget/BatchCopyBudget`, bodyParams)
      .then(({ data }) => {
        this.setState({ showCopyBudgetModal: false });

        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;

        ToastrService.info(intl.formatMessage(messages.GlobalActionStarted), undefined, onClick);
      })
      .catch(handleHttpError(intl));
  };

  getColumns = () => {
    const { budgets, initialSearchParams: { columns = [] } = {} } = this.state;
    return columns.length ? budgets.filter(x => columns.indexOf(x.id) >= 0) : budgets;
  };

  createExcelReport = () => {
    const { intl } = this.props;
    const { currentSearchParams } = this.state;
    const languageCode = intl.locale;

    const currentFilters = this.getBudgetFilters();

    this.setState({ creatingReport: true });

    const connectionId = SignalRService.getConnectionId();

    return api.base
      .post(`${config.appUrls.api}/api/budget/Report`, currentFilters, {
        params: {
          connectionId,
          languageCode,
          desc: currentSearchParams.desc,
          sort: currentSearchParams.sortKey,
        },
      })
      .then(() => {
        this.setState({ creatingReport: false });
        ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
      })
      .catch(error => {
        this.setState({ creatingReport: false });
        handleHttpError(intl)(error);
      });
  };
  jobUpdateHandler = job => {
    if (
      (job.jobType === 42 || job.jobType === 43 || job.jobType === 45) &&
      (job.status === 2 || job.status === 3)
    ) {
      this.setState(state => ({
        initialSearchParams: {
          ...state.initialSearchParams,
          page: 0,
          // forces refresh of table
          random: new Date().getTime(),
        },
      }));
    }
  };

  showBudgetGraphModal = (data, budget, year, isAmount, plannedDuration = null) => {
    const { intl } = this.props;
    this.setState({
      graphModalData: {
        data,
        budget,
        plannedDuration,
        label: `${intl.formatMessage(
          isAmount ? messages.ActualAmount : messages.ActualHours,
        )} - ${year}`,
      },
      showBudgetGraphModal: true,
    });
  };
  closeBudgetGraphModal = () => {
    this.setState({
      showBudgetGraphModal: false,
      graphModalData: null,
    });
  };
  getYearFromFilters = fallback => {
    const { initialSearchParams: { budgetFilters: { year } = {} } = {} } = this.state;
    if (year && year > 2000) return year;
    if (typeof fallback !== 'undefined') return fallback;

    return moment.utc().year();
  };

  handleColumnSelection = items => {
    this.setState(({ hideableColumns }) => ({
      ...handleHiddenColumnsSelection(
        items,
        hideableColumns,
        columnSelectionStorageKey,
        columnDefinitions,
      ),
      showColumnSelectionModal: false,
    }));
  };

  render() {
    const {
      items,
      initialSearchParams,
      itemToDelete,
      allSelected,
      showUpdateBudgetAmountModal,
      showUpdateBudgetHoursModal,
      showCopyBudgetModal,
      creatingReport,
      showBudgetGraphModal,
      graphModalData,
      showColumnSelectionModal,
      hideableColumns,
      columnStatus,
      totals,
    } = this.state;
    const { intl, invoiceDialogsSvc, stateService } = this.props;
    const canViewAmounts = authService.hasPermission({
      functionality: 'BudgetAmounts',
      privilege: privileges.Read,
    });
    const canViewHours = authService.hasPermission({
      functionality: 'BudgetHours',
      privilege: privileges.Read,
    });
    const canUpdateAmounts = authService.hasPermission({
      functionality: 'BudgetAmounts',
      privilege: privileges.Update,
    });
    const canUpdateHours = authService.hasPermission({
      functionality: 'BudgetHours',
      privilege: privileges.Update,
    });

    const showAssignments = hasAssignments();
    const hasData = items && items.length > 0;

    return (
      <div id="budget-overview">
        {showBudgetGraphModal && (
          <OkCancelModal
            title={intl.formatMessage(messages.Budget)}
            show
            onClose={this.closeBudgetGraphModal}
            onSave={this.closeBudgetGraphModal}
          >
            <div className="form-horizontal">
              <div>
                <BigBudgetGraph
                  data={graphModalData.data}
                  budget={graphModalData.budget}
                  plannedDuration={graphModalData.plannedDuration}
                  dataLabel={graphModalData.label}
                  intl={intl}
                />
              </div>
            </div>
          </OkCancelModal>
        )}
        {showUpdateBudgetAmountModal && (
          <UpdateBudgetAmountModal
            intl={intl}
            show
            onClose={() => this.setState({ showUpdateBudgetAmountModal: false })}
            onSave={amountUpdate => this.updateInvoiceStreamAmountBatch(amountUpdate)}
            year={this.getYearFromFilters()}
          />
        )}
        {showUpdateBudgetHoursModal && (
          <UpdateBudgetHoursModal
            intl={intl}
            show
            onClose={() => this.setState({ showUpdateBudgetHoursModal: false })}
            onSave={data => this.updateInvoiceStreamHoursBatch(data)}
            year={this.getYearFromFilters()}
          />
        )}
        {showCopyBudgetModal && (
          <CopyBudgetModal
            intl={intl}
            show
            onClose={() => this.setState({ showCopyBudgetModal: false })}
            onSave={data => this.copyBudgetBatch(data)}
            year={this.getYearFromFilters(null)}
          />
        )}
        <SimplePanel
          title={intl.formatMessage(messages.BudgetOverview)}
          article="5974708498460"
          buttons={
            <CanRead on="settings">
              <a href="/#/settings/#BudgetSettings" className="pull-right white">
                <i className="fa fa-cogs" />
                <span className="margin5L hidden-xs">
                  <FormattedMessage {...messages.OpenBudgetSettings} />
                </span>
              </a>
            </CanRead>
          }
        >
          <div className="info-block container-fluid">
            <BudgetOverviewFilters
              filters={initialSearchParams.budgetFilters}
              onFilterChange={filters => this.filtersChanged(filters)}
              countSelected={this.getCountSelectedRowsFormatted()}
              totals={totals}
            />
          </div>
        </SimplePanel>
        <div className="row btn-row">
          <div className="col-md-12 pull-right content-right">
            <Button
              btnClassName="btn btn-xs btn-default margin10R"
              onClick={() => this.setState({ showColumnSelectionModal: true })}
            >
              <span className="margin5R">
                <FaCog className="react-icon" />
              </span>
              {intl.formatMessage(messages.ColumnConfig)}
            </Button>
            <Button
              btnClassName="btn btn-xs btn-default margin10R"
              onClick={() => this.createExcelReport()}
              disabled={!hasData || creatingReport}
            >
              <span className="margin5R">
                <FaFileExcel className="file-icon" />
              </span>
              {intl.formatMessage(messages.ExportToExcel)}
            </Button>
            {canUpdateAmounts || canUpdateHours ? (
              <GlobalActionsButton
                onUpdateBudgetAmount={() => this.setState({ showUpdateBudgetAmountModal: true })}
                onUpdateBudgetHours={() => this.setState({ showUpdateBudgetHoursModal: true })}
                onCopyBudget={() => this.setState({ showCopyBudgetModal: true })}
                disabled={this.isGlobalActionsDisabled()}
                canUpdateAmounts={canUpdateAmounts}
                canUpdateHours={canUpdateHours}
              />
            ) : null}
          </div>
        </div>
        <ScrollableTable
          tableClassName="table-grid margin50B"
          loadItems={searchParams => this.loadItems(searchParams)}
          initialSearchParams={initialSearchParams}
          items={items}
          renderRow={item => {
            const { columnStatus } = this.state;
            return (
              <BudgetRow
                key={item.invoiceStream.id}
                value={item}
                intl={intl}
                selected={!!item.selected}
                handleSelect={this.handleSelect}
                showAssignments={showAssignments}
                canUpdateAmounts={canUpdateAmounts}
                canUpdateHours={canUpdateHours}
                canViewAmounts={canViewAmounts}
                canViewHours={canViewHours}
                onUpdateBudget={this.onUpdateBudget}
                onDeleteBudget={this.onDeleteBudget}
                invoiceDialogsSvc={invoiceDialogsSvc}
                showBudgetGraphModal={this.showBudgetGraphModal}
                stateService={stateService}
                columnStatus={columnStatus}
              />
            );
          }}
          noDataLabel={<FormattedMessage {...messages.NoDataFound} />}
        >
          <ColumnHeader
            title={
              <Checkbox extraClass="margin2L" onClick={this.handleSelectAll} value={allSelected} />
            }
          />
          <ColumnHeader
            title={intl.formatMessage(messages.Relation)}
            sortKey="relation.displayName,budget.year"
          />
          {showAssignments ? (
            <ColumnHeader
              title={intl.formatMessage(messages.Assignment)}
              sortKey="invoiceStream.assignmentTemplateName,invoiceStream.assignmentName,relation.displayName,budget.year"
            />
          ) : null}
          <ColumnHeader title={intl.formatMessage(messages.Year)} className="col-period" />
          {canViewAmounts ? (
            <ColumnHeader
              title={intl.formatMessage(messages.BudgetAmount)}
              className="col-sm text-right"
              sortKey="budget.budgetAmount,relation.displayName,budget.year"
              visible={columnStatus.budgetAmount}
            />
          ) : null}
          {canViewAmounts ? (
            <ColumnHeader
              title={intl.formatMessage(messages.ActualAmount)}
              className="col-sm text-right"
              sortKey="budget.actualAmount,relation.displayName,budget.year"
              visible={columnStatus.actualAmount}
            />
          ) : null}
          {canViewAmounts ? (
            <ColumnHeader
              title={intl.formatMessage(messages.RemainingAmount)}
              className="col-sm text-right"
              sortKey="budget.remainingAmount,relation.displayName,budget.year"
              visible={columnStatus.remainingAmount}
            />
          ) : null}
          {canViewAmounts ? (
            <ColumnHeader
              title={intl.formatMessage(messages.BudgetAmountUsedPct)}
              className="col-sm text-right"
              sortKey="budget.budgetAmountUsed,relation.displayName,budget.year"
              visible={columnStatus.budgetAmountUsedPct}
            />
          ) : null}
          {/* Graph */}
          {canViewAmounts ? (
            <ColumnHeader className="col-graph" visible={columnStatus.budgetAmountUsedPct} />
          ) : null}
          {canViewHours ? (
            <ColumnHeader
              title={intl.formatMessage(messages.BudgetHours)}
              className="col-sm text-right"
              sortKey="budget.budgetHours,relation.displayName,budget.year"
              visible={columnStatus.budgetHours}
            />
          ) : null}
          {canViewHours ? (
            <ColumnHeader
              title={intl.formatMessage(messages.ActualHours)}
              className="col-sm text-right"
              sortKey="budget.actualHours,relation.displayName,budget.year"
              visible={columnStatus.actualHours}
            />
          ) : null}
          {canViewHours ? (
            <ColumnHeader
              title={intl.formatMessage(messages.RemainingHours)}
              className="col-sm text-right"
              sortKey="budget.remainingHours,relation.displayName,budget.year"
              visible={columnStatus.remainingHours}
            />
          ) : null}
          {canViewHours ? (
            <ColumnHeader
              title={intl.formatMessage(messages.PlannedDurationHours)}
              className="col-sm text-right"
              visible={columnStatus.plannedDuration}
            />
          ) : null}
          {canViewHours ? (
            <ColumnHeader
              title={intl.formatMessage(messages.BudgetVsPlannedDeviationPct)}
              className="col-sm text-right"
              visible={columnStatus.budgetVsPlannedDeviationPct}
            />
          ) : null}
          {canViewHours ? (
            <ColumnHeader
              title={intl.formatMessage(messages.BudgetHoursUsedPct)}
              className="col-sm text-right"
              sortKey="budget.budgetHoursUsed,relation.displayName,budget.year"
              visible={columnStatus.budgetHoursUsedPct}
            />
          ) : null}
          {/* Graph */}
          {canViewHours ? (
            <ColumnHeader className="col-graph" visible={columnStatus.budgetHoursUsedPct} />
          ) : null}
          <ColumnHeader className="col-sm " />
        </ScrollableTable>

        {itemToDelete ? (
          <YesNoModal
            show
            title={intl.formatMessage(messages.DeleteBudget)}
            message={intl.formatMessage(messages.DeleteBudgetMsg)}
            onNo={() => this.setState({ itemToDelete: null })}
            onSave={this.doDelete}
          />
        ) : null}

        {showColumnSelectionModal && (
          <CheckboxListModal
            title={intl.formatMessage(messages.SelectColumns)}
            items={hideableColumns}
            onSave={this.handleColumnSelection}
            onClose={() => this.setState({ showColumnSelectionModal: false })}
            show={showColumnSelectionModal}
            sortByLabel={false}
          />
        )}
      </div>
    );
  }
}

BudgetOverviewContainer.propTypes = {
  intl: intlShape.isRequired,
  searchParams: PropTypes.shape({}),
  stateService: PropTypes.instanceOf(StateService).isRequired,
};
BudgetOverviewContainer.defaultProps = {
  searchParams: null,
};
export default injectStateService(injectIntl(BudgetOverviewContainer));
